import type { ReactNode, FormEvent } from 'react';
import clsx from 'clsx';

import { Button, Card, Checkbox, Input } from '@lib/ui';

export interface AuthFormProps {
  /** Form title */
  title?: string;
  /** Form subtitle/description */
  subtitle?: string;
  /** Logo element to display above the form */
  logo?: ReactNode;
  /** Called when form is submitted with username and password */
  onSubmit?: (data: { username: string; password: string }) => void;
  /** Error message to display */
  errorMessage?: string;
  /** Whether the form is in loading state */
  isLoading?: boolean;
  /** Show "Remember me" checkbox */
  showRememberMe?: boolean;
  /** Show "Forgot password" link */
  showForgotPassword?: boolean;
  /** Called when forgot password is clicked */
  onForgotPassword?: () => void;
  /** Social login buttons configuration */
  socialLogins?: Array<{
    name: string;
    icon: ReactNode;
    onClick: () => void;
  }>;
  /** Footer links */
  footerLinks?: Array<{
    label: string;
    href: string;
  }>;
  /** Link to signup page */
  signupLink?: {
    text: string;
    linkText: string;
    href: string;
  };
  /** Additional className for the container */
  className?: string;
}

export function AuthForm({
  title = 'Welcome Back',
  subtitle = 'Please sign in to continue',
  logo,
  onSubmit,
  errorMessage,
  isLoading,
  showRememberMe = true,
  showForgotPassword = true,
  onForgotPassword,
  socialLogins,
  footerLinks,
  signupLink,
  className,
}: AuthFormProps) {
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    onSubmit?.({
      username: formData.get('username') as string,
      password: formData.get('password') as string,
    });
  };

  return (
    <div className={clsx('w-full max-w-[26rem] p-4 sm:px-5', className)}>
      <div className="text-center">
        {logo && <div className="mx-auto">{logo}</div>}
        <div className="mt-4">
          <h2 className="text-2xl font-semibold text-gray-600 dark:text-dark-100">{title}</h2>
          <p className="text-gray-400 dark:text-dark-300">{subtitle}</p>
        </div>
      </div>

      <Card className="mt-5 rounded-lg p-5 lg:p-7">
        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="space-y-4">
            <Input
              label="Username"
              name="username"
              placeholder="Enter Username"
              prefix={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1}
                  stroke="currentColor"
                  className="size-5 transition-colors duration-200"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                  />
                </svg>
              }
            />
            <Input
              label="Password"
              name="password"
              type="password"
              placeholder="Enter Password"
              prefix={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1}
                  stroke="currentColor"
                  className="size-5 transition-colors duration-200"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                  />
                </svg>
              }
            />
          </div>

          {errorMessage && (
            <div className="mt-2">
              <p className="text-sm text-error">{errorMessage}</p>
            </div>
          )}

          {(showRememberMe || showForgotPassword) && (
            <div className="mt-4 flex items-center justify-between space-x-2">
              {showRememberMe && <Checkbox label="Remember me" />}
              {showForgotPassword && (
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-xs text-gray-400 transition-colors hover:text-gray-800 focus:text-gray-800 dark:text-dark-300 dark:hover:text-dark-100 dark:focus:text-dark-100"
                >
                  Forgot Password?
                </button>
              )}
            </div>
          )}

          <Button type="submit" className="mt-5 w-full" color="primary" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        {signupLink && (
          <div className="mt-4 text-center text-xs-plus">
            <p className="line-clamp-1">
              <span>{signupLink.text}</span>{' '}
              <a
                className="text-primary-600 transition-colors hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-600"
                href={signupLink.href}
              >
                {signupLink.linkText}
              </a>
            </p>
          </div>
        )}

        {socialLogins && socialLogins.length > 0 && (
          <>
            <div className="my-7 flex items-center space-x-3 text-xs">
              <div className="h-px flex-1 bg-gray-200 dark:bg-dark-500"></div>
              <p>OR</p>
              <div className="h-px flex-1 bg-gray-200 dark:bg-dark-500"></div>
            </div>
            <div className="flex gap-4">
              {socialLogins.map((social) => (
                <Button
                  key={social.name}
                  className="h-10 flex-1 gap-3"
                  variant="outlined"
                  onClick={social.onClick}
                >
                  {social.icon}
                  <span>{social.name}</span>
                </Button>
              ))}
            </div>
          </>
        )}
      </Card>

      {footerLinks && footerLinks.length > 0 && (
        <div className="mt-8 flex justify-center text-xs text-gray-400 dark:text-dark-300">
          {footerLinks.map((link, index) => (
            <span key={link.href} className="flex items-center">
              {index > 0 && (
                <div className="mx-2.5 my-0.5 w-px bg-gray-200 dark:bg-dark-500 h-3"></div>
              )}
              <a href={link.href}>{link.label}</a>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

AuthForm.displayName = 'AuthForm';
